import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { API_URL } from "../config";
import { showToast } from "../components/toast";
import LoadingSpinner from "../components/LoadingSpinner";

const AIVoiceCallModal = ({
  isOpen,
  onClose,
  subscriber,
  onCallInitiated,
}) => {
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [callHistory, setCallHistory] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);

  useEffect(() => {
    if (isOpen && subscriber) {
      fetchCallHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, subscriber]);

  if (!isOpen || !subscriber) return null;

  const getAuthHeader = () => {
    const token = localStorage.getItem("authToken");
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  };

  const fetchCallHistory = async () => {
    try {
      setHistoryLoading(true);
      const res = await axios.get(
        `${API_URL}voiceCall/logs?mobile=${subscriber.mobileno || ""}&subUid=${subscriber.id || ""}`,
        getAuthHeader()
      );
      const list = res.data?.items || [];
      setCallHistory(list);
      if (list.length > 0) {
        setSelectedCall(list[0]);
      }
    } catch (err) {
      console.error("Failed to load call logs:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handlePlaceCall = async () => {
    try {
      setLoading(true);
      const payload = {
        subUid: String(subscriber.id || ""),
        mobile: subscriber.mobileno,
        name: subscriber.fullname || "Valued Customer",
        leadStatus: subscriber.lead_status || "new",
      };

      const res = await axios.post(
        `${API_URL}voiceCall/instant`,
        payload,
        getAuthHeader()
      );

      showToast.success("AI Call placed! Bot is ringing customer...");
      if (onCallInitiated) onCallInitiated(res.data);
      fetchCallHistory();
    } catch (err) {
      console.error("Error triggering call:", err);
      showToast.error(
        err?.response?.data?.message || "Failed to trigger AI call."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show"></div>
      <div
        className="modal fade show"
        style={{ display: "block" }}
        tabIndex="-1"
        role="dialog"
        onClick={onClose}
      >
        <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header d-flex justify-content-between align-items-center">
              <div>
                <h5 className="modal-title font-weight-bold d-inline-block">AI Voice Call</h5>
                <span className="text-muted ms-2" style={{ fontSize: "0.85rem" }}>
                  {subscriber.fullname} ({subscriber.mobileno})
                </span>
              </div>
              <button
                type="button"
                className="btn btn-default float-end"
                onClick={onClose}
              >
                &times;
              </button>
            </div>

            <div className="modal-body">
              <div className="d-flex align-items-center gap-2 mb-3">
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  disabled={loading}
                  onClick={handlePlaceCall}
                >
                  <FontAwesomeIcon icon={faPhone} /> {loading ? "Dialing..." : "Dial Call Now"}
                </button>
                <button
                  type="button"
                  className="btn btn-default btn-sm ms-2"
                  onClick={fetchCallHistory}
                  disabled={historyLoading}
                >
                  <FontAwesomeIcon icon={faRefresh} /> Refresh Logs
                </button>
              </div>

              <h6 className="font-weight-bold mb-2">Call History & Transcripts</h6>

              {historyLoading ? (
                <div className="text-center p-4">
                  <LoadingSpinner />
                </div>
              ) : callHistory.length === 0 ? (
                <div className="alert alert-info text-center p-3 mb-0">
                  No previous AI calls recorded for this number.
                </div>
              ) : (
                <div className="row border rounded m-0 p-2">
                  <div className="col-md-4 border-right" style={{ maxHeight: "350px", overflowY: "auto" }}>
                    {callHistory.map((call) => (
                      <div
                        key={call.id}
                        onClick={() => setSelectedCall(call)}
                        className={`p-2 mb-1 rounded ${
                          selectedCall?.id === call.id ? "bg-primary text-white" : "bg-light text-dark"
                        }`}
                        style={{ cursor: "pointer", fontSize: "12px" }}
                      >
                        <div className="font-weight-bold">{call.outcome || call.status || "Call"}</div>
                        <div className={selectedCall?.id === call.id ? "text-white-50" : "text-muted"}>
                          {call.durationSeconds ? `${call.durationSeconds}s` : "0s"} &bull;{" "}
                          {call.createdOn ? new Date(call.createdOn).toLocaleDateString() : "-"}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="col-md-8" style={{ maxHeight: "350px", overflowY: "auto" }}>
                    {selectedCall ? (
                      <div>
                        <div className="mb-2">
                          <span className="font-weight-bold">Status: </span>
                          <span
                            className={`badge ${
                              selectedCall.status === "completed" ? "badge-success" : "badge-secondary"
                            }`}
                            style={{ padding: "4px 8px" }}
                          >
                            {selectedCall.status}
                          </span>
                          {selectedCall.outcome && (
                            <span className="ms-2">
                              Disposition: <strong>{selectedCall.outcome}</strong>
                            </span>
                          )}
                        </div>

                        {selectedCall.recordingUrl && (
                          <div className="mb-3">
                            <label className="font-weight-bold small d-block mb-1">Audio Recording:</label>
                            <audio controls style={{ width: "100%", height: "36px" }} src={selectedCall.recordingUrl} />
                          </div>
                        )}

                        {selectedCall.summary && (
                          <div className="alert alert-light p-2 mb-3 small border">
                            <strong>Summary:</strong> {selectedCall.summary}
                          </div>
                        )}

                        <div>
                          <label className="font-weight-bold small d-block mb-1">Conversation Transcript:</label>
                          <div
                            className="p-2 border rounded bg-light small"
                            style={{ maxHeight: "180px", overflowY: "auto" }}
                          >
                            {Array.isArray(selectedCall.transcript) && selectedCall.transcript.length > 0 ? (
                              selectedCall.transcript.map((t, idx) => (
                                <div key={idx} className="mb-2">
                                  <strong>{t.role === "agent" ? "Agent: " : "Customer: "}</strong>
                                  <span>{t.text}</span>
                                </div>
                              ))
                            ) : (
                              <span className="text-muted">No transcript available.</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-muted small p-3">Select a call to view details</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-default" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AIVoiceCallModal;
